/**
 * Maps Algolia / UI filter attribute keys to one of four visual categories
 * (location, salary, company, job) so pills and modal rows share the same palette.
 */

export type FilterVisualCategory = 'location' | 'salary' | 'company' | 'job';

const ATTRIBUTE_TO_CATEGORY: Record<string, FilterVisualCategory> = {
  remoteness: 'location',
  location: 'location',
  regionTags: 'location',

  salaryRangeMin: 'salary',
  salaryRangeMax: 'salary',
  vacationWeeks: 'salary',

  companyId: 'company',
  companyIndustry: 'company',
  /** Company size (All filters modal slider). */
  companyHeadcountRangeMax: 'company',
  isRecruitmentCompany: 'company',
  isConsultingCompany: 'company',

  jobType: 'job',
  normalisedRoleLevel: 'job',
  normalisedTitle: 'job',
  requiredLanguages: 'job',
  benefits: 'job',
  skillsAndTechnologies: 'job',
  field: 'job',
  educationLevel: 'job',
  peopleManagementTier: 'job',
  isDriversLicenseRequired: 'job',
  isOpenApplication: 'job',
  minimumYearsOfExperience: 'job',
  onlyStatedYears: 'job',
  isEveningWork: 'job',
  isWeekendWork: 'job',
  isShiftWork: 'job',
  weeklyHoursAtMost: 'job',
  weeklyHoursAtLeast: 'job',
};

export function normalizeFilterAttributeKey(attribute: string): string {
  if (attribute.startsWith('exclude_')) {
    return attribute.slice('exclude_'.length);
  }
  return attribute;
}

export function getFilterVisualCategory(attribute: string): FilterVisualCategory | null {
  const key = normalizeFilterAttributeKey(attribute);
  return ATTRIBUTE_TO_CATEGORY[key] ?? null;
}

export type FilterModalCategoryColors = {
  bg: string;
  text: string;
  border: string;
  checkmark: string;
  badge: string;
};

const MODAL_CATEGORY_COLORS: Record<FilterVisualCategory, FilterModalCategoryColors> = {
  location: {
    bg: 'bg-jc-filter-location-bg',
    text: 'text-jc-filter-location-text',
    border: 'border-jc-filter-location-border',
    checkmark: 'text-jc-filter-location-text',
    badge:
      'bg-jc-filter-location-bg text-jc-filter-location-text border border-jc-filter-location-border',
  },
  salary: {
    bg: 'bg-jc-filter-salary-bg',
    text: 'text-jc-filter-salary-text',
    border: 'border-jc-filter-salary-border',
    checkmark: 'text-jc-filter-salary-text',
    badge: 'bg-jc-filter-salary-bg text-jc-filter-salary-text border border-jc-filter-salary-border',
  },
  company: {
    bg: 'bg-jc-filter-company-bg',
    text: 'text-jc-filter-company-text',
    border: 'border-jc-filter-company-border',
    checkmark: 'text-jc-filter-company-text',
    badge:
      'bg-jc-filter-company-bg text-jc-filter-company-text border border-jc-filter-company-border',
  },
  job: {
    bg: 'bg-jc-filter-job-bg',
    text: 'text-jc-filter-job-text',
    border: 'border-jc-filter-job-border',
    checkmark: 'text-jc-filter-job-text',
    badge: 'bg-jc-filter-job-bg text-jc-filter-job-text border border-jc-filter-job-border',
  },
};

/** Row / checkbox styling inside filter modals (CustomRefinementList). */
export function getFilterModalCategoryColors(attribute: string): FilterModalCategoryColors {
  const cat = getFilterVisualCategory(attribute) ?? 'job';
  return MODAL_CATEGORY_COLORS[cat];
}
